import { EmptyState } from '@ordo/ui/empty-state'
import { School } from 'lucide-react'
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router'

import { useSchoolOnboardingGate } from '@/api/school-onboarding'
import { AppShell } from '@/components/app-shell'
import { CafeteriaKeyboardHandoffBoundary } from '@/components/cafeteria/keyboard-handoff'
import { ProtectedRoute, UnauthRoute } from '@/components/protected-route'
import { RouteError } from '@/components/route-error'
import { useKioskCommandPoller } from '@/hooks/use-kiosk-command-poller'
import { pickHomeNavItem } from '@/lib/nav-home'
import { NO_SCHOOLS_EMPTY, SupportEmailButton } from '@/lib/support'
import { AcceptInvitePage } from '@/pages/accept-invite'
import { BillingDefaultRedirect, BillingPage } from '@/pages/billing'
import { DeliverablesPage } from '@/pages/deliverables'
import { FamilyDayPage } from '@/pages/family-day'
import { ForgotPasswordPage } from '@/pages/forgot-password'
import { GetStartedPage } from '@/pages/get-started'
import { ImpersonateLandPage } from '@/pages/impersonate-land'
import { MyFamilyPage } from '@/pages/my-family'
import { OrdersDefaultRedirect, OrdersPage } from '@/pages/orders'
import { PairPage } from '@/pages/pair'
import { ProductionRecordsPage } from '@/pages/production-records'
import { RecipesPage } from '@/pages/recipes'
import { SchoolDayPage } from '@/pages/school-day'
import { SchoolOnboardingHome } from '@/pages/school-onboarding-home'
import { SetPasswordPage } from '@/pages/set-password'
import { SettingsDefaultRedirect, SettingsPage } from '@/pages/settings'
import { ShoppingListPage } from '@/pages/shopping-list'
import { parseShoppingListTab } from '@/pages/shopping-list-helpers'
import { SignIn } from '@/pages/signin'
import { SignupPage } from '@/pages/signup'
import { TaskDetailPage } from '@/pages/task-detail'
import { TasksPage } from '@/pages/tasks'
import { VerifyEmailChangePage } from '@/pages/verify-email-change'
import { familyDayLoader } from '@/routes/family-day-loader'
import { lazyElement } from '@/routes/lazy'
import { CafeteriaInventorySkeleton } from '@/skeletons/cafeteria-inventory-skeleton'
import { CafeteriaServeSkeleton } from '@/skeletons/cafeteria-serve-skeleton'
import { CafeteriaTemperaturesSkeleton } from '@/skeletons/cafeteria-temperatures-skeleton'
import { CafeteriaTodaySkeleton } from '@/skeletons/cafeteria-today-skeleton'
import { FamilyPrintSkeleton } from '@/skeletons/family-print-skeleton'
import { OnboardingWizardSkeleton } from '@/skeletons/onboarding-wizard-skeleton'
import { RoutePageSkeleton } from '@/skeletons/route-page-skeleton'
import { TemperatureLogSkeleton } from '@/skeletons/temperature-log-skeleton'
import { needsOnboarding, useProfile } from '@/stores/profile'

// Route-level code splitting (routes/lazy.tsx). LAZY = infrequent or
// heavy-dependency surfaces: the cafeteria tree (drags dexie/cafeteria-db),
// kiosk, print artifacts, wizards/admin flows, checkouts (Stripe React
// bindings), and the dev mockup. STATIC = the common parent path (signin →
// HomePage switch pages → day views): those must paint without a chunk
// round-trip, and the HomePage slug switch renders them inline.

/**
 * Routing for the consolidated apps/ordo app. The 10 canonical
 * sidebar items (per `@ordo/access/nav.ts`) get one route each.
 * Branching pages (Orders / Billing / Settings) render their
 * variants inline based on the user's profileCodes.
 *
 * `/kiosk` is a fullscreen POS surface — renders OUTSIDE the
 * OrdoAppShell (no sidebar / topbar) so tablets in hardware
 * lockdown have minimal chrome.
 *
 * `/profile` is retired (PROD-27) — it redirects to
 * `/settings/account`, where the avatar menu now points too.
 *
 * `/` redirects to the first item in the user's resolved nav once
 * the profile loads; until then it's a thin "Welcome" landing
 * managed inside HomeRedirect.
 */
/** `<Navigate>` that carries the current query string to the target path. */
function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} replace />
}

/**
 * Root layout — mounts once for EVERY route, shell or not. The kiosk
 * command poller lives here (not in AppShell) because paired kiosks run
 * the /cafeteria + /kiosk subtrees, which render outside the shell; a
 * force-update parked by the Devices page must reach them wherever they
 * are. The hook no-ops unless `profile.kioskContext.paired`.
 */
function RootLayout() {
  useKioskCommandPoller()
  return <Outlet />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    // Covers every route. Besides giving users a recoverable screen, this
    // displaces react-router's DefaultErrorComponent and the
    // "Error handled by React Router default ErrorBoundary:" console.error
    // it emits — 552 client-exception events / 7d, one alert hash per
    // render error. Reporting stays in <RouterProvider onError> (main.tsx).
    errorElement: <RouteError />,
    children: [
      // Public — signin + password-reset surfaces (Plan 50)
      {
        path: 'signin',
        element: (
          <UnauthRoute>
            <SignIn />
          </UnauthRoute>
        ),
      },
      // Alias hand-typed by users; keeps `?email=` and friends intact.
      { path: 'login', element: <RedirectWithSearch to="/signin" /> },
      {
        // Invited account-claim page — invite emails link here with
        // `?email=` prefilled. Distinct from /get-started (the organic
        // school-first funnel).
        path: 'signup',
        element: (
          <UnauthRoute>
            <SignupPage />
          </UnauthRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <UnauthRoute>
            <ForgotPasswordPage />
          </UnauthRoute>
        ),
      },
      {
        // The set-password page is reached via a multipass-issued token
        // link, so it's always accessed without an existing session.
        path: 'set-password',
        element: (
          <UnauthRoute>
            <SetPasswordPage />
          </UnauthRoute>
        ),
      },
      {
        // Plan 36 — Accept invite landing. Reached via the magic-link
        // emailed/SMSed by the invitation dispatcher. The page POSTs
        // the token immediately + writes the session cookie on
        // success, so it sits OUTSIDE the auth-protected routes.
        path: 'accept-invite',
        element: <AcceptInvitePage />,
      },

      // Email-change verification landing — reached from the link mailed
      // to the NEW address. Outside the auth wrappers (same reasoning as
      // accept-invite: a redirect must never eat the token); the page
      // handles the signed-out case itself.
      { path: 'verify-email-change', element: <VerifyEmailChangePage /> },

      // Plan 25 — `/pair?token=...` kiosk pair landing. Sits OUTSIDE
      // the auth-protected shell because a freshly-paired tablet has
      // no multipass cookie yet — the kiosk bearer IS the credential
      // and it gets persisted to localStorage by this page.
      { path: 'pair', element: <PairPage /> },

      // Per-tab impersonation ("Access school/chef view") landing. Sits OUTSIDE
      // the auth-protected shell: the tab has no session yet — the fragment
      // tokens ARE the credential and this page persists them to sessionStorage.
      { path: 'impersonate/land', element: <ImpersonateLandPage /> },

      // Create Account — the public signup page. No-auth; menus require
      // an account, so this is the direct entry from sign-in's "Get started".
      { path: 'get-started', element: <GetStartedPage /> },

      // The route page the printed delivery route's one code opens (the Oct 20
      // all-hands). Sits OUTSIDE the auth-protected shell: a driver may have no
      // Ordo account, and the signed link in the path IS the credential.
      {
        path: 'route/:token',
        element: lazyElement(() => import('@/pages/route-page'), 'RoutePage', <RoutePageSkeleton />),
      },

      // Fullscreen kiosk/cafeteria modes — gated, but outside the shell.
      // The whole subtree is lazy: it drags the dexie/cafeteria-db offline
      // stack, which parents/chefs never need. The SW precaches lazy chunks,
      // so cafeteria offline still works after the first visit.
      {
        Component: ProtectedRoute,
        children: [
          { path: 'kiosk', element: lazyElement(() => import('@/pages/kiosk-mode'), 'KioskModePage') },
          // Every cafeteria route shares ONE keyboard-handoff bridge (PROD-1278,
          // item 1): Today arms it inside the tap that opens Serve, and Serve — a
          // sibling route OUTSIDE the mode layout — moves the focus (and the iOS
          // keyboard with it) onto its search once that input exists. The bridge
          // has to outlive that navigation, so it mounts here, above both.
          {
            Component: CafeteriaKeyboardHandoffBoundary,
            children: [
              // Cafeteria mode shell (own sidebar + Today/History mini-nav).
              {
                element: lazyElement(() => import('@/components/cafeteria/mode-layout'), 'CafeteriaModeLayout'),
                children: [
                  { path: 'cafeteria', element: <Navigate to="/cafeteria/today" replace /> },
                  {
                    path: 'cafeteria/today',
                    element: lazyElement(
                      () => import('@/pages/cafeteria/today'),
                      'CafeteriaTodayPage',
                      <CafeteriaTodaySkeleton />,
                    ),
                  },
                  {
                    path: 'cafeteria/inventory',
                    element: lazyElement(
                      () => import('@/pages/cafeteria/inventory'),
                      'CafeteriaInventoryPage',
                      <CafeteriaInventorySkeleton />,
                    ),
                  },
                  {
                    path: 'cafeteria/temperatures',
                    element: lazyElement(
                      () => import('@/pages/cafeteria/temperatures'),
                      'CafeteriaTemperaturesPage',
                      <CafeteriaTemperaturesSkeleton />,
                    ),
                  },
                  {
                    path: 'cafeteria/history',
                    element: lazyElement(() => import('@/pages/cafeteria/history'), 'CafeteriaHistoryPage'),
                  },
                ],
              },
              // Focused full-page serve surface (no mode sidebar). The old
              // deliveries / complete wizards are the school admin's Transport
              // logs / Service records tasks on /production now (PROD-1277).
              {
                path: 'cafeteria/serve',
                element: lazyElement(
                  () => import('@/pages/cafeteria/serve'),
                  'ServeMealsPage',
                  <CafeteriaServeSkeleton />,
                ),
              },
              // Printable serving sheets — a VISIBLE print-artifact page (USDA
              // production-records pattern), opened in a new tab from the serve
              // page's print dialog. No shell, no hidden print portals.
              {
                path: 'cafeteria/serve/print',
                element: lazyElement(() => import('@/pages/cafeteria/serve-print'), 'CafeteriaServePrintPage'),
              },
              {
                path: 'cafeteria/runbook-offline',
                element: lazyElement(() => import('@/pages/cafeteria-runbook-offline'), 'CafeteriaRunbookOfflinePage'),
              },
            ],
          },
        ],
      },

      // Printable USDA Production Records — gated, but outside the
      // shell so the print artifact carries no sidebar/topbar chrome.
      // Opened in a new tab from the detail drawer's Print button.
      {
        Component: ProtectedRoute,
        children: [
          {
            path: 'production/:id/print',
            element: lazyElement(
              () => import('@/pages/usda-production-records-print'),
              'UsdaProductionRecordsPrintPage',
            ),
          },
          {
            // Batch prep-form view — one document per record in
            // ?locationId&from&to, unsigned days as blank write-in forms.
            path: 'production/print',
            element: lazyElement(
              () => import('@/pages/usda-production-records-print-batch'),
              'UsdaProductionRecordsPrintBatchPage',
            ),
          },
          {
            // Unified school print packet — record forms + pass-out sheets,
            // day-interleaved. Every school print button funnels here.
            path: 'school/print',
            element: lazyElement(() => import('@/pages/school-print'), 'SchoolPrintPage'),
          },
          {
            // A parent's print of a child's week or month of orders
            // (PROD-1064), opened from the Print dialog on Orders.
            path: 'orders/family/print',
            element: lazyElement(
              () => import('@/pages/orders/family-print'),
              'FamilyPrintPage',
              <FamilyPrintSkeleton />,
            ),
          },
        ],
      },

      // Printable Kitchen Prep Packet (Plan 16) — opens in a new tab
      // from the Print prep packet sheet. Same shell-less pattern as
      // the USDA print view; the chef uses Cmd+P to produce the PDF.
      {
        Component: ProtectedRoute,
        children: [
          {
            path: 'kitchen-prep-packet/print',
            element: lazyElement(() => import('@/pages/kitchen-prep-packet-print'), 'KitchenPrepPacketPrintPage'),
          },
        ],
      },

      // Focused full-page workflows — gated, but OUTSIDE the shell (own
      // header + close button), not main-nav surfaces. Temp logs are
      // reached from a task card; the onboarding wizard from the gate
      // card's "Complete Onboarding Tasks" button (and the aggregate
      // "Complete onboarding" inbox row after provisioning).
      {
        Component: ProtectedRoute,
        children: [
          {
            path: 'tasks/:taskId/temperature-log',
            element: lazyElement(
              () => import('@/pages/temperature-log'),
              'TemperatureLogPage',
              <TemperatureLogSkeleton />,
            ),
          },
          {
            path: 'onboarding',
            element: lazyElement(
              () => import('@/pages/onboarding-wizard'),
              'OnboardingWizardPage',
              <OnboardingWizardSkeleton />,
            ),
          },
          {
            path: 'onboarding/:taskId',
            element: lazyElement(
              () => import('@/pages/onboarding-wizard'),
              'OnboardingWizardPage',
              <OnboardingWizardSkeleton />,
            ),
          },
          // Legacy per-task URLs (old bookmarks + notification payloads).
          { path: 'tasks/:taskId/onboarding', element: <LegacyOnboardingTaskRedirect /> },
        ],
      },

      // Standard authenticated routes, wrapped in OrdoAppShell
      {
        Component: ProtectedRoute,
        children: [
          {
            Component: AppShell,
            children: [
              { index: true, element: <HomePage /> },
              { path: 'family', element: <MyFamilyPage /> },
              // Plan 42* — per-student medical documentation surface.
              // PARENT-only read view + "Request a special diet
              // approval" CTA (no upload — uploads route through
              // staff via apps/customers).
              {
                path: 'family/medical/:studentId',
                element: lazyElement(() => import('@/pages/medical/student'), 'MedicalDocumentationPage'),
              },
              // FAMILY auto-order setup/manage lives from each My Family
              // student card. No standalone auto-order index page.
              { path: 'family/auto-orders', element: <Navigate to="/family" replace /> },
              {
                path: 'family/auto-orders/new',
                element: lazyElement(() => import('@/pages/family-subscriptions/new'), 'FamilySubscriptionNewPage'),
              },
              {
                path: 'family/auto-orders/:id',
                element: lazyElement(
                  () => import('@/pages/family-subscriptions/detail'),
                  'FamilySubscriptionDetailPage',
                ),
              },
              { path: 'family/subscriptions', element: <Navigate to="/family" replace /> },
              { path: 'family/subscriptions/new', element: <Navigate to="/family/auto-orders/new" replace /> },
              { path: 'family/subscriptions/:id', element: <FamilySubscriptionLegacyRedirect /> },
              { path: 'shopping-list', element: <ShoppingListLegacyTabRoute /> },
              { path: 'recipes', element: <RecipesPage /> },
              { path: 'deliverables', element: <DeliverablesPage /> },
              // Deep-link target for a single task notification (PROD-28). A
              // direct AppShell child OUTSIDE OnboardingGateGuard: it renders
              // with the sidebar/topbar chrome but is never blocked by the
              // school onboarding gate that wraps the /tasks list, so a parent
              // following a notification always lands on the task.
              { path: 'tasks/:id', element: <TaskDetailPage /> },
              // Orders / Billing / Production Records / Settings are gated
              // behind onboarding completion for school admins. The guard
              // redirects to `/` (the onboarding gate) while onboarding is in
              // progress; everyone else falls straight through.
              {
                Component: OnboardingGateGuard,
                children: [
                  // While onboarding is active the Tasks page behaves like
                  // Orders/Production Records: it renders the gate card.
                  // Onboarding tasks themselves live in the /onboarding
                  // wizard, never in this inbox.
                  { path: 'tasks', element: <TasksPage /> },
                  {
                    path: 'notifications',
                    element: lazyElement(() => import('@/pages/notifications'), 'NotificationsPage'),
                  },
                  // Orders + three URL forks per Plan 03. Bare /orders
                  // redirects to the user's default variant via
                  // OrdersDefaultRedirect; the explicit forks always
                  // render exactly one variant and surface the
                  // BranchingSwitcher at the top for multi-hat users.
                  { path: 'orders', element: <OrdersDefaultRedirect /> },
                  { path: 'orders/family', element: <OrdersPage /> },
                  { path: 'orders/family/day', element: <FamilyDayPage />, loader: familyDayLoader },
                  {
                    path: 'orders/family/checkout',
                    element: lazyElement(() => import('@/pages/family-checkout'), 'FamilyCheckoutPage'),
                  },
                  { path: 'orders/school', element: <OrdersPage /> },
                  // Full-page single-day order entry (the mobile day view).
                  { path: 'orders/school/day', element: <SchoolDayPage /> },
                  // Full-page Review & checkout for the multi-week cart.
                  {
                    path: 'orders/school/checkout',
                    element: lazyElement(() => import('@/pages/school-checkout'), 'SchoolCheckoutPage'),
                  },
                  // Read-only service-details page for past/completed days (PROD-1123 v2).
                  {
                    path: 'orders/school/service',
                    element: lazyElement(() => import('@/pages/school-service-details'), 'SchoolServiceDetailsPage'),
                  },
                  { path: 'orders/kitchen', element: <OrdersPage /> },
                  { path: 'production', element: <ProductionRecordsPage /> },
                  // Full-page record view (two-part compliance document).
                  // `/production/print` (batch, shell-less) is registered
                  // above; the static segment outranks this param route.
                  {
                    path: 'production/:id',
                    element: lazyElement(() => import('@/pages/production-record'), 'ProductionRecordPage'),
                  },
                  // Billing + forks per Plan 03. `/billing/family` covers
                  // both balance + invoice families (the page itself
                  // surfaces sub-panels by funding model).
                  { path: 'billing', element: <BillingDefaultRedirect /> },
                  { path: 'billing/family', element: <BillingPage /> },
                  { path: 'billing/family-balance', element: <BillingPage /> },
                  { path: 'billing/school', element: <BillingPage /> },
                  { path: 'billing/kitchen', element: <BillingPage /> },
                  // Settings + forks per Plan 03.
                  { path: 'settings', element: <SettingsDefaultRedirect /> },
                  { path: 'settings/my-school', element: <SettingsPage /> },
                  // School AR as a Settings tab — the same panel
                  // /billing/school renders. School admins don't carry
                  // `invoice.read`, so the Billing nav item never shows
                  // for them; this fork is how they reach it.
                  { path: 'settings/billing', element: <SettingsPage /> },
                  // Student import (roster CSV wizard). Not mounted on the
                  // settings page — this standalone route is the surface
                  // task/onboarding flows deep-link to.
                  {
                    path: 'settings/my-school/roster-import',
                    element: lazyElement(() => import('@/pages/settings/my-school/roster-import'), 'RosterImportPage'),
                  },
                  {
                    path: 'settings/my-school/invitations',
                    element: lazyElement(() => import('@/pages/invitation-dispatcher'), 'InvitationDispatcherPage'),
                  },
                  // Plan 25 — Devices (kiosk pairing). Hidden from the
                  // /settings/my-school hub for now; this URL is the only
                  // surface, for breadcrumbs / agent intents / ops links.
                  {
                    path: 'settings/my-school/devices',
                    element: lazyElement(() => import('@/pages/settings/devices'), 'DevicesPage'),
                  },
                  // Plan 15 — SCHOOL_ADMIN auto-order wizard + cards. Lists
                  // SCHOOL_DEFAULT-kind subscriptions; the wizard writes them
                  // into the same in-memory store the Plan 37 runner reads.
                  {
                    path: 'settings/my-school/auto-orders',
                    element: lazyElement(() => import('@/pages/settings/auto-orders'), 'AutoOrdersPage'),
                  },
                  {
                    path: 'settings/my-school/auto-orders/new',
                    element: lazyElement(() => import('@/pages/settings/auto-orders-wizard'), 'AutoOrdersNewPage'),
                  },
                  {
                    path: 'settings/my-school/auto-orders/:id/edit',
                    element: lazyElement(() => import('@/pages/settings/auto-orders-wizard'), 'AutoOrdersEditPage'),
                  },
                  // The district-wide OVS split view. Exists because each
                  // campus's settings page was an island: an admin edited
                  // district percentages with no way to see that two of three
                  // campuses had the split off and were shipping 100%.
                  {
                    path: 'settings/my-school/district-split',
                    element: lazyElement(() => import('@/pages/settings/district-split'), 'DistrictSplitPage'),
                  },
                  { path: 'settings/my-kitchen', element: <SettingsPage /> },
                  // Plan 34 — chef-side transparency view of the IngredientSupplyLink
                  // rows Ordo has negotiated. Read-only; authoring lives in apps/menu-builder-demo.
                  {
                    path: 'settings/my-kitchen/supply-links',
                    element: lazyElement(
                      () => import('@/pages/settings/my-kitchen/supply-links'),
                      'MyKitchenSupplyLinksPage',
                    ),
                  },
                ],
              },
              // Personal Account settings (My profile + Security &
              // Sign-In + Notifications, PROD-27). Deliberately OUTSIDE
              // the onboarding gate — like the retired /profile page
              // before it, personal settings stay reachable while a
              // school is onboarding. /profile 301s here so old links
              // (and the avatar menu muscle memory) keep working.
              { path: 'settings/account', element: <SettingsPage /> },
              {
                path: 'settings/catalog/contains-review',
                element: lazyElement(
                  () => import('@/pages/settings/catalog/contains-review'),
                  'ContainsReviewSettingsPage',
                ),
              },
              { path: 'profile', element: <Navigate to="/settings/account" replace /> },
            ],
          },
        ],
      },

      // Dev-only — data-less signup/login flow mockup (Plans 06/07). Spread
      // conditionally so it is not routable in production builds; the page
      // component also hard-guards on import.meta.env.DEV.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev-signup-mockup',
              element: lazyElement(() => import('@/pages/dev-signup-mockup'), 'DevSignupMockupPage'),
            },
          ]
        : []),

      // Anything else → back to root (which redirects to first nav item)
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])

/**
 * `/` renders the user's persona-default page INLINE (no navigation,
 * no URL change). Picks the first non-priority nav item (Orders for a
 * PARENT, Tasks-or-Orders for a SCHOOL_ADMIN, etc.). Falls back to the
 * first item of any zone if the user only has priority items, and to
 * `NoMembershipsLanding` if they have no nav items at all.
 *
 * Why "render inline" instead of `<Navigate to={target.href}>`:
 * keeping the URL at `/` means the home button + sidebar brand link
 * always land on a clean, bookmarkable home — not a deep link like
 * `/orders/family` that exposes routing internals.
 *
 * Multi-hat users (ORDO_ADMIN) on `/` see the Orders multi-variant
 * shell (the same component renders without a `urlVariant`, falling
 * into the multi-hat default branch). That matches the spirit of
 * a panopticon home.
 */
function HomePage() {
  const { profile, loading } = useProfile()
  const gate = useSchoolOnboardingGate()
  if (loading || !profile || gate.loading) {
    return null
  }
  // Onboarding still outstanding = never finished signing up. Today the only
  // way to be here is an account imported from the legacy ordo app: it holds a
  // multipass identity (old password) but none of the profile the signup
  // funnel collects. Send them to finish it — /get-started detects the session
  // and resumes (email + password steps already satisfied, name prefilled)
  // instead of dead-ending them on NoMembershipsLanding.
  if (needsOnboarding(profile)) {
    return <Navigate to="/get-started" replace />
  }
  // While onboarding is in progress, the school's normal surfaces have no
  // provisioned data yet — show the onboarding gate (school name + progress +
  // "Complete Onboarding Tasks") instead of the persona-default page.
  if (gate.gated && gate.school) {
    return <SchoolOnboardingHome school={gate.school} />
  }
  const items = profile.nav.items.filter((item) => item.slug !== 'cafeteria')
  // Never land on Settings while a work item exists — a cafeteria
  // worker's nav is Tasks (priority) + Settings, and their home is Tasks.
  const target = pickHomeNavItem(items)
  if (!target) {
    return <NoMembershipsLanding />
  }
  // Slug → component map. Mirrors the route table below. Adding a new
  // canonical nav slug requires adding both a route line AND a case
  // here so `/` keeps working for users whose first nav item is that
  // slug. A test in @ordo/access pins the canonical slugs (9 since the Aug 2026 chef-sidebar split added Deliverables).
  switch (target.slug) {
    case 'tasks':
      return <TasksPage />
    case 'orders':
      return <OrdersPage />
    case 'my-family':
      return <MyFamilyPage />
    case 'shopping-list':
      return <ShoppingListPage />
    case 'recipes':
      return <RecipesPage />
    case 'deliverables':
      return <DeliverablesPage />
    case 'cafeteria':
      return <Navigate to="/cafeteria/today" replace />
    case 'production-records':
      return <ProductionRecordsPage />
    case 'billing':
      return <BillingPage />
    case 'settings':
      return <SettingsPage />
    default:
      return <NoMembershipsLanding />
  }
}

/**
 * Route guard for surfaces gated until the Ordo team provisions the school
 * (Tasks / Orders / Billing / Production Records / Settings). While gated, these
 * render the onboarding gate / "we're setting you up" waiting screen IN PLACE
 * — so the rows stay clickable and the school always lands on its status from
 * any nav item, rather than hitting a redirect to a `/` it can't navigate to.
 * Non-gated users (provisioned schools, parents, chefs, …) fall through.
 */
function OnboardingGateGuard() {
  const gate = useSchoolOnboardingGate()
  if (gate.loading) {
    return null
  }
  if (gate.gated && gate.school) {
    return <SchoolOnboardingHome school={gate.school} />
  }
  return <Outlet />
}

function FamilySubscriptionLegacyRedirect() {
  const params = useParams<{ id: string }>()
  return <Navigate to={`/family/auto-orders/${encodeURIComponent(params.id ?? '')}`} replace />
}

function LegacyOnboardingTaskRedirect() {
  const params = useParams<{ taskId: string }>()
  return <Navigate to={`/onboarding/${encodeURIComponent(params.taskId ?? '')}`} replace />
}

/**
 * Explicit legacy-tab → route map. The tab values happen to equal the
 * new page paths today, but nothing guarantees that — a path rename
 * must update this map, which fails loudly here instead of bouncing
 * old links off the catch-all.
 */
const SHOPPING_LIST_LEGACY_TAB_ROUTES = {
  recipes: '/recipes',
  deliverables: '/deliverables',
} as const

/**
 * /shopping-list with legacy pre-Aug-2026 `?tab=` handling. The three
 * kitchen lists were tabs on this one page before the chef-sidebar
 * split; old `?tab=recipes|deliverables` deep links redirect to the
 * standalone pages those tabs became, carrying the remaining params
 * (`?date=&view=`) so a shared link still lands on the same range.
 * No (or unknown) tab renders the Ingredients page in place.
 */
function ShoppingListLegacyTabRoute() {
  const [searchParams] = useSearchParams()
  const legacyTab = parseShoppingListTab(searchParams.get('tab'))
  if (legacyTab === 'ingredients') {
    return <ShoppingListPage />
  }
  const params = new URLSearchParams(searchParams)
  params.delete('tab')
  return <Navigate to={{ pathname: SHOPPING_LIST_LEGACY_TAB_ROUTES[legacyTab], search: params.toString() }} replace />
}

function NoMembershipsLanding() {
  return (
    <div className="grid h-full place-items-center p-8">
      <EmptyState
        icon={<School />}
        title={NO_SCHOOLS_EMPTY.title}
        description={NO_SCHOOLS_EMPTY.description}
        action={<SupportEmailButton subject="I'm signed in but have no schools linked" />}
      />
    </div>
  )
}
